import React, { useState, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { apiFetch } from "@/hooks/apiFetch"
import { formatToDevanagariStyle } from "@/utilities/helper"

const ProductSelector = ({
  open,
  onClose,
  onSelect,
  selectedProductIds = [],
}) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [selected, setSelected] = useState(new Set(selectedProductIds))
  const [page, setPage] = useState(1)

  const { data: productsResponse, isLoading } = useQuery({
    queryKey: ["products-selector", page, searchQuery],
    queryFn: ({ signal }) =>
      apiFetch(`product?page=${page}&per_page=50&search=${searchQuery}`, {
        signal,
      }),
    enabled: open,
  })

  const products = productsResponse?.data?.data || []

  const handleToggle = (productId) => {
    const newSelected = new Set(selected)
    if (newSelected.has(productId)) {
      newSelected.delete(productId)
    } else {
      newSelected.add(productId)
    }
    setSelected(newSelected)
  }

  const handleSelectAll = () => {
    if (selected.size === products.length && products.length > 0) {
      setSelected(new Set())
    } else {
      const allIds = new Set(products.map((p) => p.id))
      setSelected(allIds)
    }
  }

  const handleConfirm = () => {
    const selectedArray = Array.from(selected).filter(
      (id) => !selectedProductIds.includes(id)
    )
    if (selectedArray.length > 0) {
      onSelect(selectedArray)
    } else {
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Products</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 2 }}>
          <TextField
            fullWidth
            label="Search Products"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPage(1)
            }}
            variant="outlined"
            size="small"
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow className="table-header-surface">
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selected.size > 0 && selected.size < products.length
                      }
                      checked={
                        products.length > 0 && selected.size === products.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.has(product.id)}
                        onChange={() => handleToggle(product.id)}
                      />
                    </TableCell>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>{product.sku}</TableCell>
                    <TableCell>{`Rs. ${formatToDevanagariStyle(product.sell_price)}`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={selected.size === 0 || isLoading}
        >
          Add Selected ({selected.size})
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ProductSelector
